/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment, useCallback, useEffect, useState, useMemo, useRef} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    normalizeSetBundleProduct,
    getUpdateBundleChildArray
} from '@salesforce/retail-react-app/app/utils/product-utils'
import Seo from '../../components/seo'

// Components
import {
    Box,
    Button,
    Stack,
    Modal,
    ModalBody,
    ModalContent,
    ModalOverlay,
    useDisclosure,
    Flex,
    Text,
    IconButton,
    Icon,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'

import {
    useProduct,
    useProducts,
    useCategory,
    useShopperCustomersMutation,
    useShopperBasketsMutation,
    useCustomerId,
    useShopperBasketsMutationHelper
} from '@salesforce/commerce-sdk-react'
import loadable from '@loadable/component'
import {Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui'
import {CustomClose} from '../../components/icons'

// Lazy load below-fold components for better performance
// Reviews - client-side only with fallback (matches live site behavior)
const PDPReviews = loadable(() => import('../../components/pdp-reviews'), {
    fallback: <Skeleton height="200px" width="100%" />
})

// Hooks
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useVariant, useDerivedProduct} from '@salesforce/retail-react-app/app/hooks'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useOptimizelyPage} from '../../hooks/use-optimizely-page'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {useHeaderContext} from '../../contexts/header-context'

// Project Components
import ProductView from '../../components/product-view'
import UnselectedVariationGroups from '../../components/unselected-variation-groups'
import ProductSetView from './partials/product-set-view'

// Lazy load FindInStore with Google Maps - only loads when user clicks "Find in Store"
// This defers ~200KB of Google Maps SDK until needed
const FindInStore = loadable(() => import('../../components/find-in-store-content'), {
    fallback: <Skeleton height="400px" width="100%" />,
    ssr: false // Client-side only since it requires user interaction
})
import InformationAccordion from './partials/information-accordion'
import MoreToDiscover from './partials/more-to-discover'

import {HTTPNotFound, HTTPError} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

// Error page
import PageNotFound from '../page-not-found'

// constant
import {
    API_ERROR_MESSAGE,
    MAX_CACHE_AGE,
    STALE_WHILE_REVALIDATE
} from '@salesforce/retail-react-app/app/constants'
import {useHistory, useLocation, useParams} from 'react-router-dom'
import {useCustomToast} from '../../contexts/custom-toast-context'
import {useWishList} from '@salesforce/retail-react-app/app/hooks/use-wish-list'
import {getWishlistId} from '../../utils/wishlist-utils'
import {useScrollTo} from '../../hooks/use-scroll-to'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

// VickyAI - Recommendations: code-split WITHOUT fallback to keep SSR for SEO
const PDPRecommendationsCarousel = loadable(() => import('./partials/pdp-recommendations-carousel'))
import {sendViewItemEvent, sendWishlistEvent, sendItemCartEvent} from '../../utils/gtm-utils'
import {sendBrazeViewItemEvent, sendBrazeCartEvent, sendBrazeWishlistEvent} from '../../utils/braze-utils'
import {isHydrated, generateAlternateUrls} from '../../utils/utils'
import {
    getBasketItemInventoryFieldsForLocale,
    getProductInventoryForLocale,
    getProductRequestParametersForLocale
} from '../../../../utils/intl-inventory'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useScript, setInitialScriptState} from '../../contexts/script-context'

const {app: globalConfig} = getConfig()

if (globalConfig.IS_NOIBU_DISABLED_PDP === 'true') {
    setInitialScriptState('noibu', false)
}

// Empty product placeholder to prevent null reference errors in hooks during navigation
const EMPTY_PRODUCT = {variants: [], variationAttributes: [], variationGroups: []}

const ProductDetail = () => {
    const {enableScript, disableScript} = useScript()
    const {app: appConfig} = getConfig()
    const {formatMessage} = useIntl()
    const history = useHistory()
    const location = useLocation()
    const einstein = useEinstein()
    const activeData = useActiveData()
    const {showCustomToast} = useCustomToast()
    const navigate = useNavigation()
    const customerId = useCustomerId()
    const {setProduct} = useHeaderContext()
    const appOrigin = useAppOrigin()
    const {locale: currentLocale} = useMultiSite()
    const basketItemInventoryFields = getBasketItemInventoryFieldsForLocale(currentLocale)
    const productRequestParameters = getProductRequestParametersForLocale(currentLocale)

    const {
        isOpen: isFindInStoreOpen,
        onOpen: onFindInStoreOpen,
        onClose: onFindInStoreClose
    } = useDisclosure()
    const {ref: reviewsRef, scrollToElement: scrollToReviews} = useScrollTo(80)

    // Toast styles - empty object to avoid overriding default fixed positioning
    const wishlistToastStyles = {}

    const {data: optimizelyData, isLoading: isOptimizelyLoading} = useOptimizelyPage('pdp')

    useEffect(() => {
        if (appConfig.IS_NOIBU_DISABLED_PDP === 'true') {
            disableScript('noibu')
        } else {
            enableScript('noibu')
        }

        return () => {
            if (appConfig.IS_NOIBU_DISABLED_PDP) {
                enableScript('noibu')
            }
        }
    }, [])

    /****************************** Basket *********************************/
    const {data: basket, isLoading: isBasketLoading} = useCurrentBasket()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const updateItemsInBasketMutation = useShopperBasketsMutation('updateItemsInBasket')
    const {res} = useServerContext()
    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }
    // SSR-only: pre-fetched Shop The Look products (used for no-JS rendering).
    const serverShopTheLookData = res?.locals?.shopTheLookData || null

    /*************************** Product Detail and Category ********************/
    const {productId} = useParams()

    // Extract product name from URL path (e.g., /p/product-name/ID.html -> "product-name")
    // This is used to determine if we're navigating to a different product vs switching variations
    const getProductNameFromPath = (pathname) => {
        const match = pathname.match(/\/p\/([^/]+)\//)
        return match ? match[1] : null
    }

    // Track product transitions for skeleton loading on PDP-to-PDP navigation
    // Only show skeleton when navigating to a DIFFERENT product (product name changes)
    // Don't show skeleton when switching variations within the same product
    const currentProductName = getProductNameFromPath(location.pathname)
    const previousProductNameRef = useRef(currentProductName)
    const [isTransitioning, setIsTransitioning] = useState(false)
    const [gtmIds, setGTMIds] = useState([])
    const [isGiftCard, setIsGiftCard] = useState(false)
    const [isAutoSelectingVariant, setIsAutoSelectingVariant] = useState(false)
    // Always use productId from path - pid is not used in URL anymore
    const effectiveProductId = productId

    // Update ref and handle navigation - only trigger transition when product NAME changes
    useEffect(() => {
        if (previousProductNameRef.current !== currentProductName && currentProductName) {
            setIsTransitioning(true)
            previousProductNameRef.current = currentProductName
        }
    }, [currentProductName])

    // Per Salesforce best practices: availability should be fetched client-side only
    // "Frequently changing content, such as remaining inventory, is unsuitable for caching"
    // See: https://developer.salesforce.com/docs/commerce/pwa-kit-managed-runtime/guide/maximizing-your-cache-hit-ratio.html
    const isClient = typeof window !== 'undefined'

    const {
        data: product,
        isLoading: isProductLoading,
        isError: isProductError,
        error: productError
    } = useProduct(
        {
            parameters: {
                id: effectiveProductId,
                perPricebook: true,
                ...productRequestParameters,
                expand: [
                    // 'availability' removed - fetched client-side below for fresh inventory
                    'promotions',
                    'prices',
                    'options',
                    'images',
                    'variations',
                    'set_products',
                    'bundled_products',
                    'recommendations',
                    'page_meta_tags'
                ],
                allImages: true
            }
        },
        {
            // When shoppers select a different variant (and the app fetches the new data),
            // the old data is still rendered (and not the skeletons).
            keepPreviousData: true
        }
    )

    // Client-side only: Fetch fresh availability data after hydration
    // This fetches the product with availability expand, which includes:
    // - Top-level inventory (stockLevel, ats, etc.)
    // - orderable flag on each variant
    // SFCC API caches availability for 60 seconds by default
    const {data: availabilityData} = useProduct(
        {
            parameters: {
                id: effectiveProductId,
                ...productRequestParameters,
                expand: ['availability', 'variations']
            }
        },
        {
            enabled: isClient && Boolean(effectiveProductId),
            staleTime: 60 * 1000 // Match SFCC's 60-second availability cache
        }
    )

    // Merge product with availability data
    // SSR: returns product as-is (no availability)
    // Client: merges fresh availability data when it loads
    const productWithAvailability = useMemo(() => {
        if (!product) return null

        const activeInventory = getProductInventoryForLocale(availabilityData, currentLocale)

        // If we have locale-specific availability data, merge it
        if (activeInventory) {
            // Create a map of variant productId -> orderable status for quick lookup
            const variantOrderableMap = new Map(
                availabilityData.variants?.map((v) => [v.productId, v.orderable]) || []
            )
            // The requested inventory is returned under `inventories`, while SFCC leaves
            // variant.orderable tied to the site's primary inventory. Override the active SKU.
            if (effectiveProductId) {
                variantOrderableMap.set(effectiveProductId, activeInventory.orderable)
            }

            // Merge orderable status into each variant
            const mergedVariants = product.variants?.map((variant) => ({
                ...variant,
                orderable: variantOrderableMap.has(variant.productId)
                    ? variantOrderableMap.get(variant.productId)
                    : variant.orderable
            }))

            const groupOrderableMap = currentLocale?.isSupportedByESW
                ? new Map(
                      (
                          availabilityData.c_eswVariationGroupOrderability
                              ?.filter((group) => typeof group.orderable === 'boolean')
                              .map((group) => [group.productId, group.orderable]) ||
                          availabilityData.variationGroups
                              ?.filter((group) => typeof group.c_eswIsOrderableSku === 'boolean')
                              .map((group) => [group.productId, group.c_eswIsOrderableSku]) ||
                          []
                      )
                  )
                : null

            const mergedVariationGroups = groupOrderableMap
                ? product.variationGroups?.map((group) =>
                      groupOrderableMap.has(group.productId)
                          ? {
                                ...group,
                                c_eswIsOrderableSku: groupOrderableMap.get(group.productId),
                                orderable: groupOrderableMap.get(group.productId)
                            }
                          : group
                  )
                : undefined

            const mergedVariationAttributes = mergedVariationGroups
                ? product.variationAttributes?.map((attribute) => {
                      if (attribute.id !== 'color') {
                          return attribute
                      }

                      return {
                          ...attribute,
                          values: attribute.values?.map((value) => {
                              const matchingGroup = mergedVariationGroups?.find(
                                  (group) => group?.variationValues?.color === value.value
                              )
                              return typeof matchingGroup?.orderable === 'boolean'
                                  ? {
                                        ...value,
                                        orderable: matchingGroup.orderable
                                    }
                                  : value
                          })
                      }
                  })
                : undefined

            return {
                ...product,
                inventory: activeInventory,
                inventoryId: activeInventory.id,
                variants: mergedVariants || product.variants,
                variationGroups: mergedVariationGroups || product.variationGroups,
                variationAttributes: mergedVariationAttributes || product.variationAttributes,
                // Merge custom attributes from availabilityData
                c_notifyMeArray: availabilityData.c_notifyMeArray || product.c_notifyMeArray,
                c_isRestock: availabilityData.c_isRestock ?? product.c_isRestock,
                c_fewItemsLeftArray:
                    availabilityData.c_fewItemsLeftArray || product.c_fewItemsLeftArray,
                c_fewItemsLeft: availabilityData.c_fewItemsLeft ?? product.c_fewItemsLeft
            }
        }

        // No availability data yet - return product as-is
        return product
    }, [product, availabilityData, currentLocale, effectiveProductId])

    // Get variant from the merged product (with fresh orderable status when available)
    // Use EMPTY_PRODUCT placeholder to prevent null reference errors during navigation
    const variant = useVariant(productWithAvailability || EMPTY_PRODUCT)

    // Client-side only: Fetch variant-level product data with promotions when a variant is selected
    // This ensures promotional prices are loaded for the specific variant (size selection)
    // BD team requirement: when switching variants, call the product API with promotions expand
    const {data: variantProductData} = useProduct(
        {
            parameters: {
                id: variant?.productId,
                perPricebook: true,
                ...productRequestParameters,
                expand: ['availability', 'prices', 'promotions']
            }
        },
        {
            enabled: isClient && Boolean(variant?.productId),
            keepPreviousData: false
        }
    )

    const isVariantProductDataFresh = useMemo(() => {
        if (!variant?.productId || !variantProductData) return false

        const variantDataId = variantProductData.productId || variantProductData.id
        return variantDataId === variant.productId
    }, [variant?.productId, variantProductData])

    // Merge variant promotional data into the product
    const productWithPromotions = useMemo(() => {
        if (!productWithAvailability) return null

        const variantInventory = getProductInventoryForLocale(variantProductData, currentLocale)

        // If we have fresh variant data, merge it into that specific variant only
        // DO NOT overwrite product-level productPromotions - that would apply the wrong
        // promotional price to other variants in findLowestPrice
        if (isVariantProductDataFresh && variant?.productId) {
            // Update only the selected variant with its availability/promotional data
            const updatedVariants = productWithAvailability.variants?.map((v) => {
                if (v.productId === variant.productId) {
                    return {
                        ...v,
                        ...(variantInventory
                            ? {
                                  inventory: variantInventory,
                                  inventoryId: variantInventory.id,
                                  orderable: variantInventory.orderable
                              }
                            : {}),
                        productPromotions:
                            variantProductData.productPromotions || v.productPromotions,
                        tieredPrices: variantProductData.tieredPrices || v.tieredPrices
                    }
                }
                return v
            })

            return {
                ...productWithAvailability,
                ...(variantInventory
                    ? {
                          inventory: variantInventory,
                          inventoryId: variantInventory.id
                      }
                    : {}),
                variants: updatedVariants || productWithAvailability.variants
                // Keep original product-level productPromotions unchanged
            }
        }

        return productWithAvailability
    }, [
        productWithAvailability,
        variantProductData,
        variant?.productId,
        isVariantProductDataFresh,
        currentLocale
    ])

    const isProductASet = product?.type.set
    const isProductABundle = product?.type.bundle
    const {variationAttributes, isOutOfStock: storefrontIsOutOfStock} = useDerivedProduct(
        productWithPromotions || product,
        isProductASet,
        isProductABundle
    )

    // Build a schema that reflects what the storefront is currently showing.
    // This avoids trusting stale/incoherent c_schemaMarkup payloads from variantProductData.
    const activeSchemaMarkup = useMemo(() => {
        const safeParse = (value) => {
            if (!value) return null
            if (typeof value === 'string') {
                try {
                    return JSON.parse(value)
                } catch (e) {
                    return null
                }
            }
            return typeof value === 'object' ? value : null
        }

        const productIdInContext = product?.id || ''
        const variantSchema = isVariantProductDataFresh ? safeParse(variantProductData?.c_schemaMarkup) : null

        const variantSchemaLooksCoherent = (() => {
            if (!variantSchema || !productIdInContext) return false
            const marker = [
                variantSchema.sku,
                variantSchema.mpn,
                variantSchema['@id'],
                variantSchema.url
            ]
                .filter(Boolean)
                .join(' ')
            return marker.includes(productIdInContext)
        })()

        const baseSchema =
            (variantSchemaLooksCoherent && variantSchema) ||
            safeParse(variant?.c_schemaMarkup) ||
            safeParse(product?.c_schemaMarkup)

        if (!baseSchema) return null

        const nextSchema = {...baseSchema}

        // Source of truth for partial selection state (e.g., color + length without size)
        // is variationAttributes.selectedValue; product/variant variationValues may only
        // represent fully resolved selections.
        const selectedValuesFromAttributes = (variationAttributes || []).reduce((acc, attr) => {
            const selectedValue = attr?.selectedValue?.value
            if (attr?.id && selectedValue) {
                acc[attr.id] = selectedValue
            }
            return acc
        }, {})

        const selectedVariationValues = {
            ...(product?.variationValues || {}),
            ...selectedValuesFromAttributes,
            ...(variant?.variationValues || {})
        }

        // Scope to variants that match the shopper's current selections (color, length, etc.).
        // This avoids mixing unrelated combinations when size is still unselected.
        const selectedScopedVariants = (productWithAvailability?.variants || []).filter((v) =>
            Object.entries(selectedVariationValues).every(([id, selectedValue]) => {
                if (!selectedValue) return true
                return v?.variationValues?.[id] === selectedValue
            })
        )

        const hasScopedOrderableSignal = selectedScopedVariants.some(
            (v) => typeof v?.orderable === 'boolean'
        )
        const inferredScopedOrderable = hasScopedOrderableSignal
            ? selectedScopedVariants.some((v) => v?.orderable)
            : undefined

        const variationDimensionIds = (product?.variationAttributes || [])
            .map((attr) => attr?.id)
            .filter((id) => id && id !== 'color')

        const buildVariantKey = (variationValues = {}) =>
            variationDimensionIds.map((id) => `${id}:${variationValues?.[id] || ''}`).join('|')

        const scopedVariantKeys = new Set(
            selectedScopedVariants
                .map((v) => buildVariantKey(v?.variationValues))
                .filter(Boolean)
        )

        const notifyMeArray = productWithAvailability?.c_notifyMeArray || product?.c_notifyMeArray || []
        const notifyMeKeys = new Set(
            notifyMeArray
                .map((entry) =>
                    variationDimensionIds
                        .map((id) => `${id}:${entry?.[id] || ''}`)
                        .join('|')
                )
                .filter(Boolean)
        )

        const isNotifyMeCoveringAllScopedVariants =
            scopedVariantKeys.size > 0 &&
            [...scopedVariantKeys].every((key) => notifyMeKeys.has(key))

        const inferredScopedOrderableByNotifyMe =
            scopedVariantKeys.size > 0 ? !isNotifyMeCoveringAllScopedVariants : undefined

        // Fallback for partial selections where backend may not have resolved a concrete variant yet:
        // if any unselected non-color attribute has zero orderable options in the UI matrix,
        // the currently selected combination is effectively out of stock.
        const inferredFromUnselectedAttributes = (() => {
            const unselectedAttributes = (variationAttributes || []).filter((attr) => {
                if (!attr?.id || attr.id === 'color') return false
                if (attr?.selectedValue?.value) return false
                return Array.isArray(attr?.values) && attr.values.length > 0
            })

            if (!unselectedAttributes.length) return undefined

            const everyUnselectedAttrHasOrderableOption = unselectedAttributes.every((attr) =>
                attr.values.some((value) => value?.orderable)
            )

            return everyUnselectedAttrHasOrderableOption
        })()

        // Availability precedence:
        // 1) If a concrete variant is resolved, trust its orderable value.
        // 2) Use orderability across variants matching current selections.
        // 3) Fall back to Notify-Me inference for the same scoped variants.
        // 4) Use unselected attribute option matrix as partial-selection fallback.
        // 5) Mirror storefront derived state when available.
        // 6) Fallback to top-level inventory.
        const orderable =
            (variant?.productId ? variant?.orderable : undefined) ??
            inferredScopedOrderable ??
            inferredScopedOrderableByNotifyMe ??
            inferredFromUnselectedAttributes ??
            (typeof storefrontIsOutOfStock === 'boolean' ? !storefrontIsOutOfStock : undefined) ??
            product?.inventory?.orderable
        const availabilityUrl =
            orderable === false ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock'
        const displayPrice = variant?.price ?? product?.price

        const offersPatch = {availability: availabilityUrl}
        if (displayPrice != null) {
            offersPatch.price = String(displayPrice)
        }

        nextSchema.offers = Array.isArray(nextSchema.offers)
            ? nextSchema.offers.map((offer) => ({...offer, ...offersPatch}))
            : nextSchema.offers
            ? {...nextSchema.offers, ...offersPatch}
            : nextSchema.offers

        const selectedColor = selectedVariationValues?.color
        const scopedGroups = (product?.imageGroups || []).filter((group) => {
            const colorAttr = group?.variationAttributes?.find((attr) => attr.id === 'color')
            if (!selectedColor) return true
            return colorAttr?.values?.some((v) => v.value === selectedColor)
        })
        const largeGroup = scopedGroups.find((group) => group.viewType === 'large')
        const fallbackGroup = scopedGroups[0]
        const images = (largeGroup || fallbackGroup)?.images?.map((img) => img.link).filter(Boolean)
        if (images?.length) {
            nextSchema.image = images
        }

        if (product?.c_canonicalURL) {
            nextSchema['@id'] = product.c_canonicalURL
        }

        return nextSchema
    }, [
        isVariantProductDataFresh,
        variantProductData?.c_schemaMarkup,
        variant?.c_schemaMarkup,
        variant?.orderable,
        variant?.price,
        variant?.variationValues?.color,
        product?.id,
        product?.c_schemaMarkup,
        product?.inventory?.orderable,
        product?.price,
        product?.variationAttributes,
        variationAttributes,
        storefrontIsOutOfStock,
        productWithAvailability?.variants,
        productWithAvailability?.c_notifyMeArray,
        product?.c_notifyMeArray,
        product?.imageGroups,
        product?.variationValues?.color,
        product?.c_canonicalURL
    ])

    // Note: Since category needs id from product detail, it can't be server side rendered atm
    // until we can do dependent query on server
    const {
        data: category,
        isError: isCategoryError,
        error: categoryError
    } = useCategory({
        parameters: {
            id: product?.primaryCategoryId,
            levels: 1
        }
    })

    /****************************** Sets and Bundles *********************************/
    const [childProductSelection, setChildProductSelection] = useState({})
    const [childProductOrderability, setChildProductOrderability] = useState({})
    const [selectedBundleQuantity, setSelectedBundleQuantity] = useState(1)
    const childProductRefs = React.useRef({})
    let bundleChildVariantIds = ''
    if (isProductABundle)
        bundleChildVariantIds = Object.keys(childProductSelection)
            ?.map((key) => childProductSelection[key].variant.productId)
            .join(',')

    const {data: bundleChildrenData} = useProducts(
        {
            parameters: {
                ids: bundleChildVariantIds,
                allImages: false,
                expand: ['availability', 'variations'],
                select: '(data.(id,inventory,master))'
            }
        },
        {
            enabled: bundleChildVariantIds?.length > 0,
            keepPreviousData: true
        }
    )

    if (isProductABundle && bundleChildrenData) {
        // Loop through the bundle children and update the inventory for variant selection
        product.bundledProducts.forEach(({product: childProduct}, index) => {
            const matchingChildProduct = bundleChildrenData.data.find(
                (bundleChild) => bundleChild.master.masterId === childProduct.id
            )
            if (matchingChildProduct) {
                product.bundledProducts[index].product = {
                    ...childProduct,
                    inventory: matchingChildProduct.inventory
                }
            }
        })
    }

    const comboProduct = isProductASet || isProductABundle ? normalizeSetBundleProduct(product) : {}

    const pageTitle = product?.pageMetaTags?.find((option) => option.id === 'title')?.value || ''

    const [primaryCategory, setPrimaryCategory] = useState(category)

    /**************** Product Variant ****************/
    const [productUrl, setProductUrl] = useState(null)

    useEffect(() => {
        // Just track the product URL for sharing/analytics - no pid needed in URL
        // pid is only needed at add-to-cart time, not for navigation
        setProductUrl(appOrigin + location.pathname)
    }, [location.pathname])

    // This page uses the `primaryCategoryId` to retrieve the category data. This attribute
    // is only available on `master` products. Since a variation will be loaded once all the
    // attributes are selected (to get the correct inventory values), the category information
    // is overridden. This will allow us to keep the initial category around until a different
    // master product is loaded.
    useEffect(() => {
        if (category) {
            setPrimaryCategory(category)
        }
    }, [category])

    // Clear transitioning state when new product data is loaded
    useEffect(() => {
        if (product) {
            setIsGiftCard(product?.c_isGiftCard || product?.c_isEGiftCard)
        }

        if (product && isTransitioning) {
            // Check if product matches either the variant ID (pid) or the variation group ID (path)
            const matchesEffectiveId = product.id === effectiveProductId
            const matchesPathId = product.id?.includes(productId.split('.')[0])
            if (matchesEffectiveId || matchesPathId) {
                setIsTransitioning(false)
            }
        }
    }, [product, isTransitioning, productId, effectiveProductId])

    /**************** Wishlist ****************/
    const {data: wishlist, isLoading: isWishlistLoading, refetch: refetchWishlist} = useWishList()
    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const createCustomerProductList = useShopperCustomersMutation('createCustomerProductList')
    const deleteCustomerProductListItem = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )

    const isInWishlist = useMemo(() => {
        if (!wishlist?.customerProductListItems || !product) return false

        return !!wishlist.customerProductListItems.find(
            (i) => i.productId === variant?.productId || i.productId === product?.id
        )
    }, [wishlist?.customerProductListItems, variant?.productId, product?.id])

    const handleAddToWishlist = async (product, variant, quantity) => {
        const listId = await getWishlistId({
            wishlist,
            customerId,
            refetchWishlist,
            createCustomerProductList
        })
        if (!listId || !customerId) {
            showError()
            return
        }
        const itemInWishlist = wishlist?.customerProductListItems?.find(
            (i) => i.productId === variant?.productId || i.productId === product?.id
        )

        const isMaximumNumberOfItemsReached = wishlist?.customerProductListItems?.length >= 100

        if (!itemInWishlist) {
            if (isMaximumNumberOfItemsReached) {
                showCustomToast({
                    message: formatMessage(
                        {
                            id: 'cart.info.full_message',
                            defaultMessage:
                                'You have reached the maximum number of items for your Wishlist. <a>See Wishlist</a>'
                        },
                        {
                            a: (chunks) => (
                                <Link
                                    href="/account/wishlist"
                                    display="block"
                                    mt={2}
                                    textDecoration="underline"
                                >
                                    {chunks}
                                </Link>
                            )
                        }
                    ),
                    styles: wishlistToastStyles
                })
            } else {
                createCustomerProductListItem.mutate(
                    {
                        parameters: {
                            listId,
                            customerId
                        },
                        body: {
                            // NOTE: API does not respect quantity, it always adds 1
                            quantity,
                            productId: variant?.productId || product?.id,
                            public: false,
                            priority: 1,
                            type: 'product'
                        }
                    },
                    {
                        onSuccess: () => {
                            showCustomToast({
                                message: formatMessage({
                                    id: 'cart.info.item_added',
                                    defaultMessage: 'Added to Wishlist'
                                }),
                                action: (
                                    <Button
                                        color="white"
                                        variant="link"
                                        onClick={() => navigate('/account/wishlist')}
                                        _hover={{
                                            color: 'white'
                                        }}
                                    >
                                        {formatMessage({
                                            id: 'cart.info.view_list',
                                            defaultMessage: 'View List'
                                        })}
                                    </Button>
                                ),
                                styles: wishlistToastStyles
                            })
                            // GTM Modification
                            sendWishlistEvent(product, false)

                            // Braze Modification - Send Braze Wishlist Event
                            sendBrazeWishlistEvent(product, false, product?.primaryCategoryId)
                        },
                        onError: () => {
                            showError()
                        }
                    }
                )
            }
        } else {
            if (!wishlist?.id) {
                showError()
                return
            }
            deleteCustomerProductListItem.mutate(
                {
                    parameters: {
                        listId: wishlist.id,
                        customerId,
                        itemId: itemInWishlist.id
                    }
                },
                {
                    onSuccess: () => {
                        showCustomToast({
                            message: formatMessage({
                                id: 'cart.info.item_removed',
                                defaultMessage: 'Item removed'
                            }),
                            styles: wishlistToastStyles
                        })
                        // GTM Modification
                        sendWishlistEvent(product, true)

                        // Braze Modification - Send Braze Wishlist Event
                        sendBrazeWishlistEvent(product, true, product?.primaryCategoryId)
                    },
                    onError: () => {
                        showError()
                    }
                }
            )
        }
    }

    const [findInStoreModalTitle, setFindInStoreModalTitle] = useState('')
    const [isVariantSelected, setIsVariantSelected] = useState(variant?.productId ? true : false)

    // Auto-select single-option attributes (size or length)
    useEffect(() => {
        if (!variationAttributes || variationAttributes.length === 0) return

        variationAttributes.forEach((attr) => {
            // Only auto-select for size or length attributes
            if (attr.id !== 'size' && attr.id !== 'length') return

            // Skip if already selected
            if (attr.selectedValue?.value) return

            // Check if there's only one available option
            const availableValues = attr.values?.filter((v) => v.orderable) || []
            if (availableValues.length === 1) {
                const singleValue = availableValues[0]
                // Navigate to the URL with the auto-selected value
                if (singleValue.href) {
                    history.replace(singleValue.href)
                    setIsAutoSelectingVariant(true)
                }
            }
        })
    }, [variationAttributes, history])

    /**************** Add To Cart ****************/
    const showError = () => {
        showCustomToast({
            message: formatMessage(API_ERROR_MESSAGE),
            status: 'error',
            styles: wishlistToastStyles
        })
    }

    const getMatchingGroup = (product) => {
        const matchingGroup = product?.variationGroups?.find(
            (group) => group?.variationValues?.color === product?.variationValues?.color
        )

        if (matchingGroup) {
            return matchingGroup.productId
        }

        return null
    }

    const handleAddToCart = async (productSelectionValues, giftCardData = null) => {
        try {
            const productItems = productSelectionValues.map(({variant, quantity}) => {
                const baseItem = {
                    productId: variant.productId,
                    productName: product?.name,
                    price: variant.price,
                    c_brand: product?.brand,
                    c_currency: product?.currency,
                    c_categories: product?.c_categories,
                    c_refinementColor: product?.c_refinementColor,
                    c_size: product?.c_size || variant.variationValues?.size,
                    c_variationGroup: getMatchingGroup(product),
                    ...basketItemInventoryFields,
                    quantity
                }

                if (product?.c_isGiftCard || product?.c_isEGiftCard) {
                    return {
                        ...baseItem,
                        c_isGiftCard: product.c_isGiftCard || false,
                        c_isEGiftCard: product.c_isEGiftCard || false,
                        ...(giftCardData || {})
                    }
                }

                return baseItem
            })

            const updatedBasket = await addItemToNewOrExistingBasket(productItems)

            // GTM Modification
            sendItemCartEvent(productItems[0], false, 'pdp')

            // Braze Modification - Send Braze Cart Event
            const brazeProduct = {
                ...productItems[0],
                imageGroups: productSelectionValues[0]?.product?.imageGroups || [],
                c_color: productSelectionValues[0]?.product?.c_color || '',
                c_relativeUrl: productSelectionValues[0]?.product?.c_relativeUrl || '',
            }
            sendBrazeCartEvent(updatedBasket, brazeProduct, 'add')

            einstein.sendAddToCart(productItems)

            // If the items were successfully added, set the return value to be used
            // by the add to cart modal.
            return productSelectionValues
        } catch (error) {
            showError(error)
        }
    }

    /**************** Product Set/Bundles Handlers ****************/
    const handleChildProductValidation = useCallback(() => {
        // Run validation for all child products. This will ensure the error
        // messages are shown.
        Object.values(childProductRefs.current).forEach(({validateOrderability}) => {
            validateOrderability({scrollErrorIntoView: false})
        })

        // Using ot state for which child products are selected, scroll to the first
        // one that isn't selected.
        const selectedProductIds = Object.keys(childProductSelection)
        const firstUnselectedProduct = comboProduct.childProducts.find(
            ({product: childProduct}) => !selectedProductIds.includes(childProduct.id)
        )?.product

        if (firstUnselectedProduct) {
            // Get the reference to the product view and scroll to it.
            const {ref} = childProductRefs.current[firstUnselectedProduct.id]

            if (ref.scrollIntoView) {
                ref.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end'
                })
            }

            return false
        }

        return true
    }, [product, childProductSelection])

    /**************** Product Bundle Handlers ****************/
    // Top level bundle does not have variants
    const handleProductBundleAddToCart = async (variant, selectedQuantity) => {
        try {
            const childProductSelections = Object.values(childProductSelection)

            const productItems = [
                {
                    productId: product.id,
                    price: product.price,
                    ...basketItemInventoryFields,
                    quantity: selectedQuantity,
                    // The add item endpoint in the shopper baskets API does not respect variant selections
                    // for bundle children, so we have to make a follow up call to update the basket
                    // with the chosen variant selections
                    bundledProductItems: childProductSelections.map((child) => {
                        return {
                            productId: child.variant.productId,
                            ...basketItemInventoryFields,
                            quantity: child.quantity
                        }
                    })
                }
            ]

            const res = await addItemToNewOrExistingBasket(productItems)

            const bundleChildMasterIds = childProductSelections.map((child) => {
                return child.product.id
            })

            // since the returned data includes all products in basket
            // here we compare list of productIds in bundleProductItems of each productItem to filter out the
            // current bundle that was last added into cart
            const currentBundle = res.productItems.find((productItem) => {
                if (!productItem.bundledProductItems?.length) return
                const bundleChildIds = productItem.bundledProductItems?.map((item) => {
                    // seek out the bundle child that still uses masterId as product id
                    return item.productId
                })
                return bundleChildIds.every((id) => bundleChildMasterIds.includes(id))
            })

            const itemsToBeUpdated = getUpdateBundleChildArray(
                currentBundle,
                childProductSelections
            )

            const itemsToBeUpdatedWithInventory = itemsToBeUpdated.map((item) => ({
                ...item,
                ...basketItemInventoryFields
            }))

            if (itemsToBeUpdatedWithInventory.length) {
                // make a follow up call to update child variant selection for product bundle
                // since add item endpoint doesn't currently consider product bundle child variants
                await updateItemsInBasketMutation.mutateAsync({
                    method: 'PATCH',
                    parameters: {
                        basketId: res.basketId
                    },
                    body: itemsToBeUpdatedWithInventory
                })
            }

            einstein.sendAddToCart(productItems)

            // GTM Modification
            sendItemCartEvent(productItems[0], false, 'pdp')

            // Braze Modification - Send Braze Cart Event
            const brazeProduct = {
                ...productItems[0],
                imageGroups: childProductSelections[0]?.product?.imageGroups || [],
                c_color: childProductSelections[0]?.product?.c_color || '',
                c_relativeUrl: childProductSelections[0]?.product?.c_relativeUrl || '',
            }
            sendBrazeCartEvent(res, brazeProduct, 'add')

            return childProductSelections
        } catch (error) {
            showError(error)
        }
    }

    /**************** Einstein ****************/
    useEffect(() => {
        setProduct(product)
        if (product && product.type.set) {
            einstein.sendViewProduct(product)
            // GTM Modification
            if (gtmIds.indexOf(product.id) < 0) {
                setGTMIds((prev) => [...prev, product.id])
                sendViewItemEvent(product)
                sendBrazeViewItemEvent(product)
            }
            const childrenProducts = product.setProducts
            childrenProducts.map((child) => {
                try {
                    einstein.sendViewProduct(child)
                } catch (err) {
                    logger.error('Einstein sendViewProduct error', {
                        namespace: 'ProductDetail.useEffect',
                        additionalProperties: {error: err, child}
                    })
                }
                activeData.sendViewProduct(category, child, 'detail')
            })
        } else if (variant && product) {
            activeData.sendViewProduct(category, product, 'detail')
        } else if (product) {
            try {
                einstein.sendViewProduct(product)
                // GTM Modification
                if (gtmIds.indexOf(product.id) < 0) {
                    setGTMIds((prev) => [...prev, product.id])
                    sendViewItemEvent(product)
                    sendBrazeViewItemEvent(product)
                }
            } catch (err) {
                logger.error('Einstein sendViewProduct error', {
                    namespace: 'ProductDetail.useEffect',
                    additionalProperties: {error: err, product}
                })
            }
            activeData.sendViewProduct(category, product, 'detail')
        }
    }, [product])

    // Memoize just the expensive variant search
    const findVariantByParams = useMemo(() => {
        // Check if we have variationGroups (normal products) or variants (gift cards)
        if (product?.variationGroups?.length) {
            return (variationParams) => {
                return product.variationGroups.find((variant) => {
                    if (!variant.variationValues) return false
                    return Object.entries(variationParams).every(([key, value]) => {
                        return variant.variationValues[key] === value
                    })
                })
            }
        } else if (product?.variants?.length) {
            // For gift cards and similar products that only have variants
            return (variationParams) => {
                return product.variants.find((variant) => {
                    if (!variant.variationValues) return false
                    return Object.entries(variationParams).every(([key, value]) => {
                        return variant.variationValues[key] === value
                    })
                })
            }
        }
        return null
    }, [product?.variationGroups, product?.variants])

    // Used for GTM
    const gtmViewItemObject = useMemo(() => {
        if (isFindInStoreOpen || !variant || !product) return null

        return {
            productId: variant?.productId,
            productName: product.name,
            price: variant.price,
            c_brand: product.brand,
            c_currency: product.currency,
            c_size: variant.variationValues?.size,
            c_color: variant.variationValues?.color,
            c_variationGroupId: getMatchingGroup(product),
            imageGroups: product.imageGroups,
            representedProduct: {
                id: variant.productId,
                c_categories: product.c_categories,
                c_refinementColor: variant.variationValues?.color
            }
        }
    }, [
        isFindInStoreOpen,
        product,
        variant?.productId,
        variant?.price,
        variant?.variationValues?.size,
        variant?.variationValues?.color
    ])

    useEffect(() => {
        if (!isFindInStoreOpen) {
            setIsVariantSelected(variant?.productId ? true : false)
        }
    }, [variant, isFindInStoreOpen])

    const lastBaseItemKeyRef = useRef(null)
    useEffect(() => {
        if (!gtmViewItemObject) return

        const key = [
            gtmViewItemObject.productId,
            gtmViewItemObject.representedProduct?.c_refinementColor,
            gtmViewItemObject.c_size,
            gtmViewItemObject.c_variationGroupId,
            gtmViewItemObject.c_color,
            gtmViewItemObject.imageGroups
        ].join('|')

        if (lastBaseItemKeyRef.current === key) return
        lastBaseItemKeyRef.current = key
        if (isAutoSelectingVariant) { 
            setIsAutoSelectingVariant(false)
        } else { 
            sendViewItemEvent(gtmViewItemObject)
            sendBrazeViewItemEvent(gtmViewItemObject)
        }
    }, [gtmViewItemObject])

    const selectedSchemaMarkup = activeSchemaMarkup || null
    const selectedSchemaMarkupJson = useMemo(
        () =>
            !selectedSchemaMarkup
                ? ''
                : typeof selectedSchemaMarkup === 'string'
                ? selectedSchemaMarkup
                : JSON.stringify(selectedSchemaMarkup),
        [selectedSchemaMarkup]
    )

    /**************** Schema Markup ****************/

    /**************** Error Handling ****************/
    if (isProductError) {
        const errorStatus = productError?.response?.status
        switch (errorStatus) {
            case 404:
                return <PageNotFound />
            default:
                throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`)
        }
    }
    if (isCategoryError) {
        const errorStatus = categoryError?.response?.status
        switch (errorStatus) {
            case 404:
                return <PageNotFound />
            default:
                throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`)
        }
    }

    return (
        <Box>
            {/* Above the fold section */}
            <Box
                className="sf-product-detail-page"
                layerStyle="PDPpage"
                data-testid="product-details-page"
                data-is-pdp="true"
                pb={{base: 12, lg: '64px'}}
            >
                <Seo title={pageTitle || product?.pageTitle} canonicalUrl={product?.c_canonicalURL}>
                    {product?.pageMetaTags?.map((tag) => {
                        if (tag.id != 'title') {
                            return (
                                <meta
                                    key={tag.id}
                                    {...(tag.id.startsWith('og:')
                                        ? {property: tag.id, content: tag.value}
                                        : {name: tag.id, content: tag.value})}
                                />
                            )
                        }
                    })}
                    {product?.c_canonicalURL && (
                        <link rel="canonical" href={product.c_canonicalURL} />
                    )}
                    {generateAlternateUrls(product?.c_alternateURLs)}
                </Seo>
                {selectedSchemaMarkupJson && (
                    <script
                        id="pdp-product-schema"
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{__html: selectedSchemaMarkupJson}}
                    />
                )}

                <Stack spacing={{base: 12, lg: 16}}>
                    {isProductASet ? (
                        <ProductSetView
                            product={productWithPromotions}
                            category={primaryCategory?.parentCategoryTree || []}
                            isLoading={isProductLoading || isTransitioning}
                        />
                    ) : isProductABundle ? (
                        <Fragment>
                            <ProductView
                                basket={basket}
                                product={productWithPromotions}
                                category={primaryCategory?.parentCategoryTree || []}
                                shopTheLookRecommendations={serverShopTheLookData}
                                addToCart={handleProductBundleAddToCart}
                                addToWishlist={handleAddToWishlist}
                                isInWishlist={isInWishlist}
                                isProductLoading={isProductLoading || isTransitioning}
                                isMasterProductChanging={isTransitioning}
                                isBasketLoading={isHydrated() && isBasketLoading}
                                isWishlistLoading={isHydrated() && isWishlistLoading}
                                validateOrderability={handleChildProductValidation}
                                childProductOrderability={childProductOrderability}
                                setSelectedBundleQuantity={setSelectedBundleQuantity}
                                onFindInStoreOpen={onFindInStoreOpen}
                                optimizelyContent={optimizelyData}
                                isOptimizelyLoading={isOptimizelyLoading}
                                onReviewsClick={scrollToReviews}
                            />

                            <hr />

                            {/* TODO: consider `childProduct.belongsToSet` */}
                            {
                                // Render the child products
                                comboProduct.childProducts.map(
                                    ({product: childProduct, quantity: childQuantity}) => (
                                        <Box key={childProduct.id} data-testid="child-product">
                                            <ProductView
                                                // Do not use an arrow function as we are manipulating the functions scope.
                                                ref={function (ref) {
                                                    // Assign the "set" scope of the ref, this is how we access the internal
                                                    // validation.
                                                    childProductRefs.current[childProduct.id] = {
                                                        ref,
                                                        validateOrderability:
                                                            this.validateOrderability
                                                    }
                                                }}
                                                basket={basket}
                                                product={childProduct}
                                                isProductPartOfSet={isProductASet}
                                                isProductPartOfBundle={isProductABundle}
                                                childOfBundleQuantity={childQuantity}
                                                selectedBundleParentQuantity={
                                                    selectedBundleQuantity
                                                }
                                                addToCart={
                                                    isProductASet
                                                        ? (variant, quantity) =>
                                                              handleAddToCart([
                                                                  {
                                                                      product: childProduct,
                                                                      variant,
                                                                      quantity
                                                                  }
                                                              ])
                                                        : null
                                                }
                                                addToWishlist={
                                                    isProductASet ? handleAddToWishlist : null
                                                }
                                                onVariantSelected={(product, variant, quantity) => {
                                                    if (quantity) {
                                                        setChildProductSelection(
                                                            (previousState) => ({
                                                                ...previousState,
                                                                [product.id]: {
                                                                    product,
                                                                    variant,
                                                                    quantity: isProductABundle
                                                                        ? childQuantity
                                                                        : quantity
                                                                }
                                                            })
                                                        )
                                                    } else {
                                                        const selections = {
                                                            ...childProductSelection
                                                        }
                                                        delete selections[product.id]
                                                        setChildProductSelection(selections)
                                                    }
                                                }}
                                                isProductLoading={
                                                    isProductLoading || isTransitioning
                                                }
                                                isMasterProductChanging={isTransitioning}
                                                isBasketLoading={isHydrated() && isBasketLoading}
                                                isWishlistLoading={
                                                    isHydrated() && isWishlistLoading
                                                }
                                                setChildProductOrderability={
                                                    setChildProductOrderability
                                                }
                                            />
                                            <InformationAccordion
                                                key={`information-accordion-${product?.master?.masterId}`}
                                                product={childProduct}
                                                optimizelyContent={optimizelyData}
                                                isOptimizelyLoading={isOptimizelyLoading}
                                            />
                                            <Box display={['none', 'none', 'none', 'block']}>
                                                <hr />
                                            </Box>
                                        </Box>
                                    )
                                )
                            }
                        </Fragment>
                    ) : (
                        <Fragment>
                            <ProductView
                                basket={basket}
                                product={productWithPromotions}
                                category={primaryCategory?.parentCategoryTree || []}
                                shopTheLookRecommendations={serverShopTheLookData}
                                addToCart={(variant, quantity, giftCardData) =>
                                    handleAddToCart([{product, variant, quantity}], giftCardData)
                                }
                                isInWishlist={isInWishlist}
                                addToWishlist={handleAddToWishlist}
                                isProductLoading={isProductLoading || isTransitioning}
                                isMasterProductChanging={isTransitioning}
                                isBasketLoading={isHydrated() && isBasketLoading}
                                isWishlistLoading={isHydrated() && isWishlistLoading}
                                onFindInStoreOpen={onFindInStoreOpen}
                                optimizelyContent={optimizelyData}
                                isOptimizelyLoading={isOptimizelyLoading}
                                onReviewsClick={scrollToReviews}
                            />
                        </Fragment>
                    )}

                    {/* VickyAI Recommendations - All carousel rendering */}
                    {appConfig?.VICKY_ENABLED === 'true' && !isGiftCard && !isProductASet && (
                        <PDPRecommendationsCarousel
                            productId={productId}
                            optimizelyData={optimizelyData}
                            isOptimizelyLoading={isOptimizelyLoading}
                            product={productWithPromotions}
                        />
                    )}
                </Stack>
                {productWithPromotions?.c_discoverCategories && (
                    <MoreToDiscover
                        pt={{base: 12, lg: 16}}
                        discoverCategories={productWithPromotions?.c_discoverCategories}
                    />
                )}
            </Box>

            {appConfig?.BV_ENABLE_RR_C2022 === 'true' && !isGiftCard && !isProductASet && (
                <Box ref={reviewsRef}>
                    <PDPReviews key={'pdp-reviews'} product={productWithPromotions} />
                </Box>
            )}
            <Modal
                isOpen={isFindInStoreOpen}
                onClose={onFindInStoreClose}
                size={!isVariantSelected ? 'xl' : 'full'}
                motionPreset="none"
                isCentered={false}
            >
                <ModalOverlay />
                <ModalContent
                    position="fixed"
                    top="0"
                    right="0"
                    height="100vh"
                    maxWidth={!isVariantSelected && '483px'}
                    boxShadow="lg"
                    borderRadius={0}
                    m={0}
                    fontFamily="HelveticaNeueLTStd"
                >
                    <ModalBody margin="auto" p={0} width="100%" overflowY="auto">
                        <Flex
                            align="center"
                            justify="space-between"
                            p={{base: '18px 16px', md: '26px 24px'}}
                            position={isVariantSelected ? {base: 'absolute'} : {base: 'sticky'}}
                            right={isVariantSelected && {base: 0}}
                            top="0"
                            zIndex="1"
                            background="white"
                            maxWidth={isVariantSelected && {md: '483px'}}
                            w={'100%'}
                        >
                            <Text fontSize="lg" fontWeight="bold" textTransform="uppercase">
                                {findInStoreModalTitle}
                            </Text>
                            <IconButton
                                onClick={onFindInStoreClose}
                                icon={<Icon as={CustomClose} />}
                                aria-label="Close modal"
                                variant="ghost"
                                size="sm"
                                background="transparent"
                                _hover={{background: 'transparent'}}
                            />
                        </Flex>
                        {isVariantSelected ? (
                            <FindInStore
                                onFindInStoreClose={onFindInStoreClose}
                                product={variant || productWithPromotions}
                                masterProduct={productWithPromotions}
                                setFindInStoreModalTitle={setFindInStoreModalTitle}
                                productName={product?.name}
                            />
                        ) : (
                            <Box p={{base: '40px 16px 16px', md: '16px 24px 16px'}}>
                                <UnselectedVariationGroups
                                    setIsVariantSelected={setIsVariantSelected}
                                    variant={variant}
                                    variationAttributes={variationAttributes}
                                    setFindInStoreModalTitle={setFindInStoreModalTitle}
                                />
                            </Box>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    )
}

ProductDetail.getTemplateName = () => 'product-detail'

ProductDetail.propTypes = {
    /**
     * The current react router match object. (Provided internally)
     */
    match: PropTypes.object
}

export default ProductDetail
